import { Link, useNavigate, useLocation } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook, Twitter } from "lucide-react";
import { CONTACT, NAV_LINKS } from "../../lib/site";
import { scrollToId } from "./SmoothScroll";

export const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const go = (link) => {
    if (link.to === "/careers") return navigate("/careers");
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(link.hash === "top" ? "top" : link.hash), 300);
    } else scrollToId(link.hash === "top" ? "top" : link.hash);
  };

  return (
    <footer className="relative bg-forest-deep border-t border-white/10 overflow-hidden" data-testid="site-footer">
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8 pt-20 pb-10">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <img src="/logo-white.png" alt="Bezalel Property Services" className="h-16 w-auto object-contain" />
            <p className="mt-6 max-w-sm text-cream/60 leading-relaxed">
              {CONTACT.tagline}. Domestic & commercial property services delivered
              with skill, reliability and honest pricing across Milton Keynes.
            </p>
            <div className="mt-7 flex gap-3">
              {[
                { Icon: Instagram, href: CONTACT.instagram, label: "Instagram" },
                { Icon: Facebook, href: CONTACT.facebook, label: "Facebook" },
                { Icon: Twitter, href: CONTACT.twitter, label: "Twitter" },
              ].map(({ Icon, href, label }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="grid place-items-center h-12 w-12 rounded-full border border-white/15 text-cream/80 hover:text-white hover:border-brand-orange hover:bg-brand-orange transition-[color,background-color,border-color] duration-300"
                  data-testid={`footer-social-${label.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="font-display uppercase tracking-[0.2em] text-xs text-brand-orange">Navigate</h4>
            <ul className="mt-5 space-y-3">
              {NAV_LINKS.map((link) => (
                <li key={link.label}>
                  <button
                    onClick={() => go(link)}
                    className="text-cream/70 hover:text-cream transition-colors duration-200 font-body"
                    data-testid={`footer-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-display uppercase tracking-[0.2em] text-xs text-brand-orange">Get In Touch</h4>
            <ul className="mt-5 space-y-4 text-cream/75">
              <li>
                <a href={`tel:${CONTACT.phoneTel}`} className="flex items-start gap-3 hover:text-cream transition-colors" data-testid="footer-phone">
                  <Phone className="h-5 w-5 mt-0.5 text-brand-orange shrink-0" />
                  <span className="font-display text-xl tracking-wide">{CONTACT.phoneDisplay}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${CONTACT.email}`} className="flex items-start gap-3 hover:text-cream transition-colors break-all" data-testid="footer-email">
                  <Mail className="h-5 w-5 mt-0.5 text-brand-orange shrink-0" />
                  <span>{CONTACT.email}</span>
                </a>
              </li>
              <li className="flex items-start gap-3">
                <MapPin className="h-5 w-5 mt-0.5 text-brand-orange shrink-0" />
                <span>{CONTACT.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-cream/50 text-sm">© 2026 Bezalel Property Services. All rights reserved.</p>
          <p className="font-display uppercase tracking-[0.2em] text-xs text-brand-orange" data-testid="footer-hashtag">{CONTACT.social}</p>
        </div>
      </div>
    </footer>
  );
};
